import _cloneDeep from 'lodash/cloneDeep';
import _isPlainObject from 'lodash/isPlainObject';
import _omit from 'lodash/omit';

import { calcWidth } from 'ravenjs/utils/css';
import { KEYS } from 'ravenjs/constants/breakpoints';
import { STICKY_COLUMN_POSITION } from 'ravenjs/constants/list';
import { get } from 'ravenjs/utils/lodash';
import { getThemeProps } from 'ravenjs/utils/theme';
import { addSchemaValue } from 'ravenjs/utils/schema';
import { renderCheck } from 'ravenjs/utils/viewport';

import { DEFAULT_COLORS } from 'constants/colors';
import { getAdvanceSearchSpecFromQueryParams, getSearchSpecFromQueryParams } from 'utils/filters';

/**
 * Add offset to all sticky columns
 *
 * @param   {Array}             columns
 * @param   {string}            position
 * @param   {string|number}     defaultOffsetWidth
 * @param   {Object}            theme
 * @return  {Array}
 */
export const addStickyOffsetInColumns = (columns, position, defaultOffsetWidth = '0px', theme) => {
    const stickyColumnOffset = {};
    KEYS.forEach(key => {
        stickyColumnOffset[key] = `calc(${defaultOffsetWidth} + 0px)`;
    });

    const parseSize = size => {
        const OFFSET_EXCEPTIONS = ['auto', 'min-content', 'max-content', 'fit-content'];
        const isException = Boolean(
            OFFSET_EXCEPTIONS.find(value => typeof size === 'string' && size.indexOf(value) !== -1)
        );

        if (isException) {
            return '0px';
        }

        const columnCount = getThemeProps('grid.columns', 12, { theme });

        if (typeof size === 'number' && size <= columnCount) {
            return `${calcWidth(size, columnCount)}%`;
        } else if (typeof value === 'number') {
            return `${size}px`;
        }

        return size;
    };

    const modifier = column => {
        const { size, sticky } = column;
        if (sticky && sticky.position === position) {
            const updatedColumn = {
                ...column,
                sticky: {
                    ...sticky,
                    offset: { ...stickyColumnOffset },
                },
            };

            if (typeof size === 'number' || typeof size === 'string') {
                const parsedSize = parseSize(size);

                Object.entries(stickyColumnOffset).forEach(([key, value]) => {
                    stickyColumnOffset[key] = `calc(${value} + ${parsedSize})`;
                });
            } else if (_isPlainObject(size)) {
                const parsedSize = {};
                let lastParsedSize = '0px';

                Object.entries(size).forEach(([key, value]) => {
                    parsedSize[key] = parseSize(value);
                });

                Object.entries(stickyColumnOffset).forEach(([key, value]) => {
                    if (parsedSize[key]) {
                        stickyColumnOffset[key] = `calc(${value} + ${parsedSize[key]})`;
                        lastParsedSize = parsedSize[key];
                    } else {
                        stickyColumnOffset[key] = `calc(${value} + ${lastParsedSize})`;
                    }
                });
            }

            return updatedColumn;
        }

        return column;
    };

    if (position === STICKY_COLUMN_POSITION.LEFT) {
        return columns.map(modifier);
    } else if (position === STICKY_COLUMN_POSITION.RIGHT) {
        const cloneColumns = [...columns];
        return cloneColumns
            .reverse()
            .map(modifier)
            .reverse();
    }

    return columns;
};

/**
 * Utility to read searchSpec and advanceSearchValue from url and apply it to DataView schema
 *
 * @param  {Object} locationObj
 * @param  {Object} schema
 * @return {*}
 */
export const applySearchSpecFromUrl = (locationObj, schema) => {
    const updatedSchema = _cloneDeep(schema);
    const advanceSearchSpec = getAdvanceSearchSpecFromQueryParams(locationObj);
    const searchSpec = getSearchSpecFromQueryParams(locationObj);

    if (advanceSearchSpec) {
        // If advance search spec is available in the url, then adding it in the list schema
        addSchemaValue(
            updatedSchema,
            'search.advancedSearch',
            updatedSchema.search.advancedSearch.map(field => ({
                ...field,
                value: advanceSearchSpec[field.searchKey] ? advanceSearchSpec[field.searchKey] : '',
            }))
        );

        let searchValue = '';

        updatedSchema.search.advancedSearch.forEach(({ key, value }) => {
            if (value) {
                searchValue = `${searchValue} ${key} : "${value}"`;
            }
        });

        addSchemaValue(updatedSchema, 'search.value', searchValue);
    } else if (searchSpec) {
        // If search spec is available in the url, then adding it in the list schema
        addSchemaValue(updatedSchema, 'search.value', searchSpec);
    }

    return updatedSchema;
};

/**
 * Get all columns that are sticky in current viewport
 *
 * @param   {Array}     columns
 * @return  {Object}
 */
export const getSelectStickyProps = columns =>
    columns.find(({ sticky }) => {
        // To check whether any column is sticky in current viewport
        if (sticky) {
            const check = get(sticky, 'check', null);
            if (check) {
                const { breakpoint, value } = check;
                return renderCheck(breakpoint, value);
            }
            return true;
        }
        return false;
    })
        ? {
              sticky: {
                  position: 'left',
                  offset: '0px',
              },
          }
        : {};

export const formatIconColors = (props = {}) => {
    const { colorProps = {} } = props;
    const colors = Object.keys(colorProps);
    colors &&
        colors.forEach(color => {
            colorProps[color] = DEFAULT_COLORS[colorProps[color]];
        });
    const defaultProps = _omit(props, ['colorProps']);
    return {
        ...defaultProps,
        ...colorProps,
    };
};
